import * as d3 from 'd3';
import cx from 'classnames';

import { Dataset } from '../../Analysis/Detail/interfaces';

import styles from './MiniHistogramD3.module.scss';

interface Props {
  container: HTMLDivElement | null;
  dataset: Dataset[];
  histogram: string;
  limits: number[];
  width: number;
  height: number;
  theme?: 'dark' | 'light';
}

const AXIS_X_PADDING = 0.05;
const TIME_TRANSITION = 300;

export class MiniHistogramD3 {
  container: HTMLDivElement | null;
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined> = d3.select<SVGSVGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
  groupMain: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));
  gRect: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));

  x: d3.ScaleBand<string> = d3.scaleBand();
  y: d3.ScaleLinear<number, number, never> = d3.scaleLinear();
  xScaleLinear: d3.ScaleLinear<number, number, never> = d3.scaleLinear();

  yAxis: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));

  bars: d3.Selection<SVGRectElement, Dataset, null, undefined> = d3.select<SVGRectElement, Dataset>(document.createElementNS('http://www.w3.org/2000/svg', 'rect'));
  
  width = 0;
  height = 0;
  margin = { top: 16, right: 2, bottom: 3, left: 2 };

  dataset: Dataset[];
  histogram: string;
  limits: number[];

  theme?: 'dark' | 'light' = 'dark';

  // eslint-disable-next-line
  constructor(props: Props) {
    const { container, dataset, height, histogram, limits, theme, width } = props;
    this.container = container;
    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    this.dataset = dataset;
    this.histogram = histogram;
    this.limits = limits;

    this.theme = theme;

    this.createSvgElement(this.width, this.height);
    this.createHistogram();
  }

  createSvgElement (width: number, height: number) {
    const { container, margin } = this;
    d3.select(container).select('svg').remove();

    this.svg = d3.select(container)
      .append('svg')
      .attr('id', 'svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    this.groupMain = this.svg
      .append('g')
      .attr('id', 'content')
      .attr('position', 'relative')
      .attr('transform', `translate( ${this.margin.left}, ${this.margin.top} )`);
  }

  renderBars = (props: { dataset: Dataset[]; limits: number[]; }) => {
    const { dataset, limits } = props;
    const { gRect, height } = this;

    // eslint-disable-next-line
    // @ts-ignore
    this.bars = gRect.selectAll('.histogram')
      .data(dataset)
      .enter()
      .append('rect')
      .attr('class', cx(styles.histogram, 'histogram'))
      .attr('x', (d: Dataset, index) => this.xScaleLinear(limits[index]))
      .attr('width', this.x.bandwidth())
      .attr('y', (d: Dataset) => this.y(d.frequency))
      .attr('height', (d: Dataset) => height - this.y(d.frequency));
  };

  createHistogram () {
    const { dataset, limits, groupMain } = this;

    this.buildAxisX();
    this.buildAxisY();

    this.gRect = groupMain.append('g')
      .attr('id', 'rect');

    this.renderBars({ dataset: dataset, limits: limits });
  }

  updateBars = (props: { dataset: Dataset[]; limits: number[]; }) => {
    const { dataset, limits } = props;
    const { gRect, height, } = this;

    // eslint-disable-next-line
    // @ts-ignore
    this.bars = gRect.selectAll('.histogram')
      .data(dataset);

    this.bars.enter()
      .append('rect')
      .merge(this.bars)
      .transition()
      .duration(TIME_TRANSITION)
      .attr('class', cx(styles.histogram, 'histogram'))
      .attr('x', (d: Dataset, index) => this.xScaleLinear(limits[index]))
      .attr('width', this.x.bandwidth())
      .attr('y', (d: Dataset) => this.y(d.frequency))
      .attr('height', (d: Dataset) => height - this.y(d.frequency));

    this.bars.exit().remove();
  };

  refreshHistogram (props: { dataset: Dataset[]; histogram: string; limits: number[]; theme: 'dark' | 'light'; }) {
    const { dataset, histogram, limits, theme } = props;

    this.dataset = dataset;
    this.histogram = histogram;
    this.limits = limits;
    this.theme = theme;

    this.buildAxisX();
    this.buildAxisY();

    this.updateBars({ dataset: dataset, limits: limits });
  }

  buildAxisX () {
    const { dataset, limits, width } = this;
    const domain = dataset.map((data, index) => index.toString());

    this.x = d3.scaleBand()
      .domain(domain)
      .range([0, width])
      .padding(AXIS_X_PADDING);

    this.xScaleLinear = d3.scaleLinear()
      .domain([limits[0], limits[limits.length - 1]])
      .range([0, width]);
  }

  buildAxisY () {
    const { dataset, height } = this;
    const maxDomain: number = d3.max([...dataset], (d) => d.frequency) || 0;
    const yMax = maxDomain + (maxDomain * 0.1);

    this.y = d3.scaleLinear()
      .domain([0, yMax])
      .range([height, 0]);
  }
}
